import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/lib/toast';
import { api } from '@/lib/api';
import { errToToast } from '@/lib/humanizeApiError';
import { useAuthStore } from '@/stores/authStore';
import { useDataStore } from '@/stores/dataStore';
import { SelectMenu, type SelectOption } from '@/components/SelectMenu';
import type { DmPrivacy } from '@/types/api';

/**
 * "Who can DM me" control for the Privacy settings tab. Auto-saves on change
 * via PATCH /api/me (no separate Save button — matches the instant feel of
 * the theme toggle). The FRIENDS tier limits new DMs to accepted friends.
 */

export function DmPrivacySelect() {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const updateCachedUser = useDataStore((s) => s.updateCachedUser);
  const [saving, setSaving] = useState(false);

  const OPTIONS: SelectOption<DmPrivacy>[] = [
    { value: 'EVERYONE', label: t('set2.dmPrivacy.everyone.label'), sublabel: t('set2.dmPrivacy.everyone.sublabel') },
    {
      value: 'FRIENDS',
      label: t('set2.dmPrivacy.friends.label'),
      sublabel: t('set2.dmPrivacy.friends.sublabel'),
    },
    {
      value: 'NOBODY',
      label: t('set2.dmPrivacy.nobody.label'),
      sublabel: t('set2.dmPrivacy.nobody.sublabel'),
    },
  ];

  const value: DmPrivacy = me?.dmPrivacy ?? 'EVERYONE';

  async function change(next: DmPrivacy) {
    if (saving || next === value) return;
    setSaving(true);
    try {
      const updated = await api.updateMe({ dmPrivacy: next });
      setUser(updated);
      updateCachedUser(updated);
    } catch (err) {
      toast.error(...errToToast(err, t('set2.dmPrivacy.saveError')));
    } finally {
      setSaving(false);
    }
  }

  return (
    <SelectMenu<DmPrivacy>
      value={value}
      options={OPTIONS}
      onChange={(v) => void change(v)}
      disabled={saving || !me}
    />
  );
}
